import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  PendingReviewsService,
  SeparationReviewSummary,
  ClassificationReviewSummary
} from 'src/app/modules/shared/services/pending-reviews/pending-reviews.service';
import { ToasterService } from 'src/app/modules/shared/services/toaster/toaster.service';
import {
  ClassificationReviewService,
  ClassificationReviewDetails,
  ClassificationApprovalPayload
} from 'src/app/modules/shared/services/classification-review/classification-review.service';
import { Household } from 'src/app/modules/shared/models/households.model';
import { HouseholdsService } from 'src/app/modules/shared/services/households/households.service';

type ReviewType = 'separation' | 'classification';

interface ReviewTab {
  type: ReviewType;
  label: string;
  count: number;
}

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss']
})
export class ReviewComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  // Loading state
  is_loading = true;

  // Data
  separation_reviews: SeparationReviewSummary[] = [];
  classification_reviews: ClassificationReviewSummary[] = [];

  // Pagination for separation reviews
  separation_current_page = 1;
  separation_page_size = 10;
  separation_total_items = 0;
  separation_total_pages = 0;

  // Pagination for classification reviews
  classification_current_page = 1;
  classification_page_size = 10;
  classification_total_items = 0;
  classification_total_pages = 0;

  // Tabs
  tabs: ReviewTab[] = [
    { type: 'separation', label: 'Separation Reviews', count: 0 },
    { type: 'classification', label: 'Classification Reviews', count: 0 }
  ];
  active_tab: ReviewType = 'separation';

  // Classification review modal state
  is_classification_modal_open = false;
  selected_classification_review: ClassificationReviewDetails | null = null;
  classification_modal_loading = false;

  // Separation review modal state
  is_separation_modal_open = false;
  selected_separation_document_id: string | null = null;

  // Household filtering
  selected_household_id: number | null = null;
  household_lookup: Map<number, Household> = new Map();

  constructor(
    private pending_reviews_service: PendingReviewsService,
    private classification_review_service: ClassificationReviewService,
    private toaster: ToasterService,
    private households_service: HouseholdsService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.pending_reviews_service.state$.subscribe(state => {
        this.is_loading = state.is_loading;
        this.separation_reviews = state.separation_reviews;
        this.classification_reviews = state.classification_reviews;
        this.selected_household_id = state.selected_household_id;

        // Debug: Log review items to see their structure
        if (this.separation_reviews.length > 0) {
          console.log('[ReviewComponent] Sample separation review:', this.separation_reviews[0]);
        }
        if (this.classification_reviews.length > 0) {
          console.log('[ReviewComponent] Sample classification review:', this.classification_reviews[0]);
        }

        // Update counts
        this.separation_total_items = state.separation_review_count;
        this.classification_total_items = state.classification_review_count;

        // Update tabs
        this.tabs[0].count = state.separation_review_count;
        this.tabs[1].count = state.classification_review_count;

        // Calculate total pages
        this.separation_total_pages = Math.ceil(this.separation_total_items / this.separation_page_size) || 0;
        this.classification_total_pages = Math.ceil(this.classification_total_items / this.classification_page_size) || 0;
      })
    );

    // Refresh data when component loads
    this.pending_reviews_service.refresh();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Tab handling
  setActiveTab(tab_type: ReviewType): void {
    this.active_tab = tab_type;
  }

  isActiveTab(tab_type: ReviewType): boolean {
    return this.active_tab === tab_type;
  }

  // Get paginated separation reviews
  get paginated_separation_reviews(): SeparationReviewSummary[] {
    const start_index = (this.separation_current_page - 1) * this.separation_page_size;
    const end_index = start_index + this.separation_page_size;
    return this.separation_reviews.slice(start_index, end_index);
  }

  // Get paginated classification reviews
  get paginated_classification_reviews(): ClassificationReviewSummary[] {
    const start_index = (this.classification_current_page - 1) * this.classification_page_size;
    const end_index = start_index + this.classification_page_size;
    return this.classification_reviews.slice(start_index, end_index);
  }

  // Separation pagination helpers
  get separation_showing_from(): number {
    if (this.separation_total_items === 0) return 0;
    return (this.separation_current_page - 1) * this.separation_page_size + 1;
  }

  get separation_showing_to(): number {
    return Math.min(this.separation_current_page * this.separation_page_size, this.separation_total_items);
  }

  separationGoToPage(page: number): void {
    if (page < 1) return;
    if (this.separation_total_pages && page > this.separation_total_pages) return;
    this.separation_current_page = page;
  }

  separationNextPage(): void {
    if (this.separation_current_page < this.separation_total_pages) {
      this.separationGoToPage(this.separation_current_page + 1);
    }
  }

  separationPrevPage(): void {
    if (this.separation_current_page > 1) {
      this.separationGoToPage(this.separation_current_page - 1);
    }
  }

  separationFirstPage(): void {
    this.separationGoToPage(1);
  }

  separationLastPage(): void {
    if (this.separation_total_pages) {
      this.separationGoToPage(this.separation_total_pages);
    }
  }

  onSeparationPageSizeChange(size: number): void {
    if (!size || size < 1) return;
    this.separation_page_size = size;
    this.separation_current_page = 1;
    this.separation_total_pages = Math.ceil(this.separation_total_items / this.separation_page_size) || 0;
  }

  // Classification pagination helpers
  get classification_showing_from(): number {
    if (this.classification_total_items === 0) return 0;
    return (this.classification_current_page - 1) * this.classification_page_size + 1;
  }

  get classification_showing_to(): number {
    return Math.min(this.classification_current_page * this.classification_page_size, this.classification_total_items);
  }

  classificationGoToPage(page: number): void {
    if (page < 1) return;
    if (this.classification_total_pages && page > this.classification_total_pages) return;
    this.classification_current_page = page;
  }

  classificationNextPage(): void {
    if (this.classification_current_page < this.classification_total_pages) {
      this.classificationGoToPage(this.classification_current_page + 1);
    }
  }

  classificationPrevPage(): void {
    if (this.classification_current_page > 1) {
      this.classificationGoToPage(this.classification_current_page - 1);
    }
  }

  classificationFirstPage(): void {
    this.classificationGoToPage(1);
  }

  classificationLastPage(): void {
    if (this.classification_total_pages) {
      this.classificationGoToPage(this.classification_total_pages);
    }
  }

  onClassificationPageSizeChange(size: number): void {
    if (!size || size < 1) return;
    this.classification_page_size = size;
    this.classification_current_page = 1;
    this.classification_total_pages = Math.ceil(this.classification_total_items / this.classification_page_size) || 0;
  }

  // Format date for display
  formatDate(date_string: string): string {
    if (!date_string) return 'N/A';
    const date = new Date(date_string);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Check if review is expiring soon (within 24 hours)
  isExpiringSoon(expires_at: string): boolean {
    if (!expires_at) return false;
    const expires_date = new Date(expires_at);
    const now = new Date();
    const hours_until_expiry = (expires_date.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hours_until_expiry > 0 && hours_until_expiry <= 24;
  }

  // Check if review has expired
  isExpired(expires_at: string): boolean {
    if (!expires_at) return false;
    const expires_date = new Date(expires_at);
    return expires_date.getTime() < Date.now();
  }

  // Get display name for a document (original filename or last 6 chars of ID)
  getDocumentDisplayName(document_id: string, original_filename?: string): string {
    if (original_filename) {
      return original_filename;
    }
    return document_id.slice(-6);
  }

  // Open the appropriate review modal based on type.
  async openReview(document_id: string, review_type: ReviewType): Promise<void> {
    if (review_type === 'separation') {
      this.openSeparationReview(document_id);
      return;
    }

    if (review_type === 'classification') {
      await this.openClassificationReview(document_id);
      return;
    }
  }

  // Open separation review modal
  openSeparationReview(document_id: string): void {
    this.selected_separation_document_id = document_id;
    this.is_separation_modal_open = true;
  }

  // Close separation review modal
  closeSeparationModal(): void {
    this.is_separation_modal_open = false;
    this.selected_separation_document_id = null;
  }

  // Handle separation review completion (all documents approved)
  async onSeparationCompleted(): Promise<void> {
    this.toaster.success('Separation review submitted', 3000);
    this.closeSeparationModal();
    await this.pending_reviews_service.refresh();
  }

  // Open classification review modal
  async openClassificationReview(document_id: string): Promise<void> {
    this.classification_modal_loading = true;

    try {
      const details = await this.classification_review_service.getReviewDetails(document_id);
      this.selected_classification_review = details;
      this.is_classification_modal_open = true;
    } catch (error) {
      console.error('[ReviewComponent] Error loading classification review:', error);
      this.toaster.warning('Failed to load classification review details', 3000);
    } finally {
      this.classification_modal_loading = false;
    }
  }

  // Close classification review modal
  closeClassificationModal(): void {
    this.is_classification_modal_open = false;
    this.selected_classification_review = null;
  }

  // Handle classification approval
  async handleClassificationApproval(payload: ClassificationApprovalPayload): Promise<void> {
    if (!this.selected_classification_review) return;

    try {
      await this.classification_review_service.approveClassification(
        this.selected_classification_review.documentId,
        payload
      );

      this.toaster.success('Classification approved successfully', 3000);
      this.closeClassificationModal();

      // Refresh the review list
      await this.pending_reviews_service.refresh();
    } catch (error) {
      console.error('[ReviewComponent] Error approving classification:', error);
      this.toaster.warning('Failed to approve classification', 3000);
    }
  }

  // Refresh reviews
  refreshReviews(): void {
    this.pending_reviews_service.refresh();
  }

  // Household filter handlers
  onHouseholdFilterChange(householdId: number | null): void {
    this.selected_household_id = householdId;

    // Reset pagination when filter changes
    this.separation_current_page = 1;
    this.classification_current_page = 1;

    // Reload reviews with new filter
    this.pending_reviews_service.loadPendingReviews(householdId);
  }

  onHouseholdLookupLoaded(lookup: Map<number, Household>): void {
    console.log('[ReviewComponent] Household lookup loaded, size:', lookup.size);
    console.log('[ReviewComponent] Household lookup map:', lookup);
    this.household_lookup = lookup;
  }

  getHouseholdName(householdId?: string | number): string {
    console.log('[ReviewComponent] getHouseholdName called with ID:', householdId);
    console.log('[ReviewComponent] Lookup map size:', this.household_lookup.size);
    console.log('[ReviewComponent] Lookup map keys:', Array.from(this.household_lookup.keys()));

    if (!householdId) {
      console.log('[ReviewComponent] No household ID provided, returning Unknown');
      return 'Unknown';
    }

    // Convert to number if string (API returns string, lookup uses number)
    const numericId = typeof householdId === 'string' ? parseInt(householdId, 10) : householdId;
    const household = this.household_lookup.get(numericId);
    console.log('[ReviewComponent] Looked up household:', household);

    return household ? household.HOUSEHOLD_NAME : 'Unknown';
  }
}
